import { AuthValuesType } from '@/src/context/types'
import { logErrorToRum } from '@/src/observability/datadog'

export const AUTH_LOGIN_ERROR_KEY = 'auth_login_error_message'

type AuthErrorLike = {
  name?: string
  message?: string
  errorCode?: string
  errorSummary?: string
  errorLink?: string
  statusCode?: number
  xhr?: {
    status?: number
    responseText?: string
    responseURL?: string
  }
}

const toAuthError = (err: unknown): AuthErrorLike => {
  if (err && typeof err === 'object') {
    return err as AuthErrorLike
  }

  return { message: String(err) }
}

export const mapAuthErrorToUiMessage = (parsedError: AuthErrorLike): string => {
  const fallbackMessage = 'Login failed. Please try again later.'
  const details = `${parsedError.errorSummary || ''} ${parsedError.message || ''}`.toLowerCase()
  const status = parsedError.statusCode || parsedError.xhr?.status

  if (details.includes('load failed') || details.includes('network') || details.includes('timeout')) {
    return 'Login failed. Please try again later.'
  }

  if (status === 401 || status === 403 || details.includes('access denied') || details.includes('unauthorized')) {
    return 'Unable to sign in with this account. Please contact your administrator.'
  }

  if (status === 429 || details.includes('too many requests')) {
    return 'Too many login attempts. Please try again later.'
  }

  return fallbackMessage
}

export async function handleLoginError(auth: AuthValuesType, err: unknown) {
  const parsedError = toAuthError(err)
  const isAuthApiError =
    parsedError.name === 'AuthApiError' || parsedError.name === 'AuthAPIError' || Boolean(parsedError.errorCode)
  const isLoadFailed = (parsedError.errorSummary || parsedError.message || '').toLowerCase().includes('load failed')

  logErrorToRum(
    err,
    {
      route: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
      origin: typeof window !== 'undefined' ? window.location.origin : 'unknown',
      oktaIssuer: process.env.NEXT_PUBLIC_OKTA_ISSUER,
      isAuthApiError,
      isLoadFailed,
      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
      errorName: parsedError.name,
      errorCode: parsedError.errorCode,
      errorSummary: parsedError.errorSummary,
      errorLink: parsedError.errorLink,
      statusCode: parsedError.statusCode,
      xhrStatus: parsedError.xhr?.status,
      xhrUrl: parsedError.xhr?.responseURL
    },
    'auth'
  )

  await auth.logout()

  const message = mapAuthErrorToUiMessage(parsedError)

  if (typeof window !== 'undefined') {
    window.sessionStorage.setItem(AUTH_LOGIN_ERROR_KEY, message)
  }
}
